import React, { useState, useMemo, Fragment } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import {
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CheckIcon,
  ChevronUpDownIcon,
} from '@heroicons/react/24/outline';
import { useData } from '../../context/DataContext';
import {
  convertCurrency,
} from '../../utils/calculations';
import {
  formatCurrency,
  formatCurrencyWithSign,
  formatPercentage,
  formatPercentageWithSign,
} from '../../utils/formatters';
import { ComposedChart, Line, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import dayjs from 'dayjs';
import '../Dashboard/Dashboard.css';

const Reports = () => {
  const { portfolios, goals, snapshots, settings, exchangeRates, properties, mortgages } = useData();

  // Projection time horizon
  const [projectionYears, setProjectionYears] = useState(5);
  // Chart data aggregation (monthly or yearly)
  const [chartAggregation, setChartAggregation] = useState('monthly');
  // Selected accounts for chart (null means all)
  const [selectedAccountIds, setSelectedAccountIds] = useState(null);
  const [isAccountDropdownOpen, setIsAccountDropdownOpen] = useState(false);

  // Calculate total portfolio value in display currency
  const totalValue = portfolios.reduce((sum, portfolio) => {
    return sum + convertCurrency(
      portfolio.currentValue,
      portfolio.currency,
      settings.displayCurrency,
      exchangeRates
    );
  }, 0);

  // Property equity
  const totalPropertyValue = (properties || []).reduce((sum, p) => sum + (p.currentValue || 0), 0);
  const totalMortgageBalance = (mortgages || []).reduce((sum, m) => sum + (m.currentBalance || 0), 0);
  const propertyEquity = totalPropertyValue - totalMortgageBalance;
  const hasRealEstate = totalPropertyValue > 0 || totalMortgageBalance > 0;
  const totalNetWorth = totalValue + propertyEquity;

  // Sort portfolios by value (descending)
  const sortedPortfolios = useMemo(() => {
    const sorted = [...portfolios];
    return sorted.sort((a, b) => {
      const aValue = convertCurrency(a.currentValue, a.currency, settings.displayCurrency, exchangeRates);
      const bValue = convertCurrency(b.currentValue, b.currency, settings.displayCurrency, exchangeRates);
      return bValue - aValue;
    });
  }, [portfolios, settings.displayCurrency, exchangeRates]);

  // Calculate changes over different time periods using snapshots
  const calculateTimeBasedChanges = useMemo(() => {
    const today = dayjs();

    const getTotalValueAtDate = (targetDate) => {
      let total = 0;
      portfolios.forEach(portfolio => {
        const portfolioSnapshots = snapshots
          .filter(s => s.portfolioId === portfolio.id && s.date <= targetDate)
          .sort((a, b) => new Date(b.date) - new Date(a.date));
        const snapshot = portfolioSnapshots[0];
        const value = snapshot ? snapshot.value : 0;
        const valueInDisplay = convertCurrency(
          value,
          portfolio.currency,
          settings.displayCurrency,
          exchangeRates
        );
        total += valueInDisplay;
      });
      return total;
    };

    let lastUpdateDate = null;
    let earliestDate = null;

    if (snapshots.length > 0) {
      const dates = snapshots.reduce((acc, snapshot) => {
        const date = snapshot.date;
        if (!acc.earliest || date < acc.earliest) acc.earliest = date;
        if (!acc.latest || date > acc.latest) acc.latest = date;
        return acc;
      }, { earliest: null, latest: null });

      earliestDate = dates.earliest;
      lastUpdateDate = dates.latest;
    }

    const weekAgoDate = today.subtract(7, 'day').format('YYYY-MM-DD');
    const monthAgoDate = today.subtract(1, 'month').format('YYYY-MM-DD');
    const sixMonthsAgoDate = today.subtract(6, 'month').format('YYYY-MM-DD');
    const yearAgoDate = today.subtract(1, 'year').format('YYYY-MM-DD');

    const earliestValue = earliestDate ? getTotalValueAtDate(earliestDate) : null;
    const lastUpdateValue = lastUpdateDate ? getTotalValueAtDate(lastUpdateDate) : null;
    const weekAgoValue = getTotalValueAtDate(weekAgoDate);
    const monthAgoValue = getTotalValueAtDate(monthAgoDate);
    const sixMonthsAgoValue = getTotalValueAtDate(sixMonthsAgoDate);
    const yearAgoValue = getTotalValueAtDate(yearAgoDate);

    const allTime = earliestValue !== null && earliestValue > 0
      ? { change: totalValue - earliestValue, percent: ((totalValue - earliestValue) / earliestValue) * 100 }
      : null;

    const sinceLastUpdate = lastUpdateValue !== null
      ? { change: totalValue - lastUpdateValue, percent: lastUpdateValue > 0 ? ((totalValue - lastUpdateValue) / lastUpdateValue) * 100 : 0, date: lastUpdateDate }
      : null;

    const sinceWeekAgo = weekAgoValue > 0
      ? { change: totalValue - weekAgoValue, percent: (totalValue - weekAgoValue) / weekAgoValue * 100 }
      : null;

    const sinceMonthAgo = monthAgoValue > 0
      ? { change: totalValue - monthAgoValue, percent: (totalValue - monthAgoValue) / monthAgoValue * 100 }
      : null;

    const sinceSixMonthsAgo = sixMonthsAgoValue > 0
      ? { change: totalValue - sixMonthsAgoValue, percent: (totalValue - sixMonthsAgoValue) / sixMonthsAgoValue * 100 }
      : null;

    const sinceYearAgo = yearAgoValue > 0
      ? { change: totalValue - yearAgoValue, percent: (totalValue - yearAgoValue) / yearAgoValue * 100 }
      : null;

    return { allTime, sinceLastUpdate, sinceWeekAgo, sinceMonthAgo, sinceSixMonthsAgo, sinceYearAgo };
  }, [totalValue, portfolios, snapshots, settings.displayCurrency, exchangeRates]);

  // Prepare combined historical and projection chart data
  const getCombinedChartData = () => {
    const filteredPortfolios = selectedAccountIds === null
      ? sortedPortfolios
      : sortedPortfolios.filter(p => selectedAccountIds.includes(p.id));

    const snapshotDates = snapshots.map(s => s.date);
    const today = dayjs();
    const allDates = [...new Set([...snapshotDates, today.format('YYYY-MM-DD')])].sort();

    const chartData = [];

    allDates.forEach(date => {
      const dataPoint = {
        date,
        displayDate: dayjs(date).format('MMM D, YYYY'),
        total: 0,
        costBasis: 0,
        lowProjection: null,
        averageProjection: null,
        optimisticProjection: null,
        isProjection: false,
      };

      filteredPortfolios.forEach(portfolio => {
        let value = 0;
        let basis = 0;

        if (date === today.format('YYYY-MM-DD')) {
          value = portfolio.currentValue;
          basis = portfolio.costBasis;
        } else {
          const portfolioSnapshots = snapshots
            .filter(s => s.portfolioId === portfolio.id && s.date <= date)
            .sort((a, b) => new Date(b.date) - new Date(a.date));
          const snapshot = portfolioSnapshots[0];
          value = snapshot ? snapshot.value : 0;
          basis = snapshot ? snapshot.costBasis : 0;
        }

        const valueInDisplay = convertCurrency(value, portfolio.currency, settings.displayCurrency, exchangeRates);
        const basisInDisplay = convertCurrency(basis, portfolio.currency, settings.displayCurrency, exchangeRates);

        dataPoint[portfolio.id] = valueInDisplay;
        dataPoint[`${portfolio.id}_name`] = portfolio.name;
        dataPoint.total += valueInDisplay;
        dataPoint.costBasis += basisInDisplay;
      });

      if (date === today.format('YYYY-MM-DD')) {
        dataPoint.lowProjection = dataPoint.total;
        dataPoint.averageProjection = dataPoint.total;
        dataPoint.optimisticProjection = dataPoint.total;
      }

      chartData.push(dataPoint);
    });

    // Calculate weighted average return rates
    let totalVal = 0;
    let weightedLowSum = 0;
    let weightedAverageSum = 0;
    let weightedOptimisticSum = 0;

    filteredPortfolios.forEach(portfolio => {
      const valueInDisplay = convertCurrency(portfolio.currentValue, portfolio.currency, settings.displayCurrency, exchangeRates);
      totalVal += valueInDisplay;

      const lowReturn = portfolio.lowReturn || 3;
      const avgReturn = portfolio.averageReturn || portfolio.estimatedReturnRate || 5;
      const optReturn = portfolio.highReturn || portfolio.optimisticReturn || (portfolio.estimatedReturnRate ? portfolio.estimatedReturnRate + 3 : 8);

      weightedLowSum += valueInDisplay * lowReturn;
      weightedAverageSum += valueInDisplay * avgReturn;
      weightedOptimisticSum += valueInDisplay * optReturn;
    });

    const lowReturnRate = totalVal > 0 ? weightedLowSum / totalVal : 3;
    const avgReturnRate = totalVal > 0 ? weightedAverageSum / totalVal : 5;
    const optReturnRate = totalVal > 0 ? weightedOptimisticSum / totalVal : 8;

    const totalMonthlyContribution = filteredPortfolios.reduce((sum, portfolio) => {
      const contribution = portfolio.monthlyContribution || 0;
      return sum + convertCurrency(contribution, portfolio.currency, settings.displayCurrency, exchangeRates);
    }, 0);

    const monthsToProject = projectionYears * 12;
    const currentTotalValue = totalVal;

    let lowValue = currentTotalValue;
    let avgValue = currentTotalValue;
    let optValue = currentTotalValue;

    for (let month = 1; month <= monthsToProject; month++) {
      const futureDate = today.add(month, 'month');
      const monthlyLowRate = lowReturnRate / 100 / 12;
      const monthlyAvgRate = avgReturnRate / 100 / 12;
      const monthlyOptRate = optReturnRate / 100 / 12;

      lowValue = lowValue * (1 + monthlyLowRate) + totalMonthlyContribution;
      avgValue = avgValue * (1 + monthlyAvgRate) + totalMonthlyContribution;
      optValue = optValue * (1 + monthlyOptRate) + totalMonthlyContribution;

      chartData.push({
        date: futureDate.format('YYYY-MM-DD'),
        displayDate: futureDate.format('MMM YYYY'),
        total: null,
        costBasis: null,
        lowProjection: lowValue,
        averageProjection: avgValue,
        optimisticProjection: optValue,
        isProjection: true,
      });
    }

    return { chartData, lowReturnRate, avgReturnRate, optReturnRate };
  };

  const { chartData: rawChartData, lowReturnRate, avgReturnRate, optReturnRate } = getCombinedChartData();

  // Aggregate chart data
  const aggregateChartData = (data, aggregation) => {
    if (aggregation === 'monthly') return data;

    const yearlyData = {};
    data.forEach(point => {
      const year = dayjs(point.date).year();
      yearlyData[year] = point;
    });

    return Object.keys(yearlyData).sort().map(year => {
      const point = { ...yearlyData[year] };
      point.displayDate = dayjs(point.date).format('YYYY');
      return point;
    });
  };

  const combinedChartData = aggregateChartData(rawChartData, chartAggregation);

  // Calculate goal target line
  const totalGoalTarget = goals.reduce((sum, goal) => {
    const goalCurrency = goal.currency || 'GBP';
    const targetInDisplay = convertCurrency(goal.targetAmount, goalCurrency, settings.displayCurrency, exchangeRates);
    return sum + targetInDisplay;
  }, 0);

  return (
    <div className="dashboard">
      {/* Header with Net Worth */}
      <div className="dashboard-header-new">
        <div className="header-content">
          <h2 className="header-label">NET WORTH</h2>

          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '32px', flexWrap: 'wrap' }}>
            {/* Total Value */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <span className="header-value">{formatCurrency(hasRealEstate ? totalNetWorth : totalValue, settings.displayCurrency, 0)}</span>
              {hasRealEstate && (
                <div style={{ display: 'flex', gap: '12px', fontSize: '11px', color: '#6b7280' }}>
                  <span>Investments: {formatCurrency(totalValue, settings.displayCurrency, 0)}</span>
                  <span>Property Equity: {formatCurrency(propertyEquity, settings.displayCurrency, 0)}</span>
                </div>
              )}
            </div>

            {/* All time */}
            {calculateTimeBasedChanges.allTime && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                <span className="header-period" style={{ fontSize: '11px', textTransform: 'uppercase' }}>All Time</span>
                <span className={`header-change ${calculateTimeBasedChanges.allTime.change >= 0 ? 'positive' : 'negative'}`} style={{ fontSize: '15px' }}>
                  {calculateTimeBasedChanges.allTime.change >= 0 ? <ArrowTrendingUpIcon style={{ width: 14, height: 14 }} /> : <ArrowTrendingDownIcon style={{ width: 14, height: 14 }} />}
                  {formatCurrencyWithSign(calculateTimeBasedChanges.allTime.change, settings.displayCurrency, 0)} ({formatPercentage(Math.abs(calculateTimeBasedChanges.allTime.percent), 0)})
                </span>
              </div>
            )}

            {/* Since last update */}
            {calculateTimeBasedChanges.sinceLastUpdate && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                <span className="header-period" style={{ fontSize: '11px', textTransform: 'uppercase' }}>
                  Since {dayjs(calculateTimeBasedChanges.sinceLastUpdate.date).format('MMM D')}
                </span>
                <span className={`header-change ${calculateTimeBasedChanges.sinceLastUpdate.change >= 0 ? 'positive' : 'negative'}`} style={{ fontSize: '15px' }}>
                  {calculateTimeBasedChanges.sinceLastUpdate.change >= 0 ? <ArrowTrendingUpIcon style={{ width: 14, height: 14 }} /> : <ArrowTrendingDownIcon style={{ width: 14, height: 14 }} />}
                  {formatCurrencyWithSign(calculateTimeBasedChanges.sinceLastUpdate.change, settings.displayCurrency, 0)} ({formatPercentage(Math.abs(calculateTimeBasedChanges.sinceLastUpdate.percent), 0)})
                </span>
              </div>
            )}

            {/* Last week */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
              <span className="header-period" style={{ fontSize: '11px', textTransform: 'uppercase' }}>Last Week</span>
              {calculateTimeBasedChanges.sinceWeekAgo ? (
                <span className={`header-change ${calculateTimeBasedChanges.sinceWeekAgo.change >= 0 ? 'positive' : 'negative'}`} style={{ fontSize: '15px' }}>
                  {calculateTimeBasedChanges.sinceWeekAgo.change >= 0 ? <ArrowTrendingUpIcon style={{ width: 14, height: 14 }} /> : <ArrowTrendingDownIcon style={{ width: 14, height: 14 }} />}
                  {formatCurrencyWithSign(calculateTimeBasedChanges.sinceWeekAgo.change, settings.displayCurrency, 0)} ({formatPercentage(Math.abs(calculateTimeBasedChanges.sinceWeekAgo.percent), 0)})
                </span>
              ) : (
                <span style={{ fontSize: '15px', color: '#9ca3af' }}>n/a</span>
              )}
            </div>

            {/* Last month */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
              <span className="header-period" style={{ fontSize: '11px', textTransform: 'uppercase' }}>Last Month</span>
              {calculateTimeBasedChanges.sinceMonthAgo ? (
                <span className={`header-change ${calculateTimeBasedChanges.sinceMonthAgo.change >= 0 ? 'positive' : 'negative'}`} style={{ fontSize: '15px' }}>
                  {calculateTimeBasedChanges.sinceMonthAgo.change >= 0 ? <ArrowTrendingUpIcon style={{ width: 14, height: 14 }} /> : <ArrowTrendingDownIcon style={{ width: 14, height: 14 }} />}
                  {formatCurrencyWithSign(calculateTimeBasedChanges.sinceMonthAgo.change, settings.displayCurrency, 0)} ({formatPercentage(Math.abs(calculateTimeBasedChanges.sinceMonthAgo.percent), 0)})
                </span>
              ) : (
                <span style={{ fontSize: '15px', color: '#9ca3af' }}>n/a</span>
              )}
            </div>

            {/* 6 Months */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
              <span className="header-period" style={{ fontSize: '11px', textTransform: 'uppercase' }}>6 Months</span>
              {calculateTimeBasedChanges.sinceSixMonthsAgo ? (
                <span className={`header-change ${calculateTimeBasedChanges.sinceSixMonthsAgo.change >= 0 ? 'positive' : 'negative'}`} style={{ fontSize: '15px' }}>
                  {calculateTimeBasedChanges.sinceSixMonthsAgo.change >= 0 ? <ArrowTrendingUpIcon style={{ width: 14, height: 14 }} /> : <ArrowTrendingDownIcon style={{ width: 14, height: 14 }} />}
                  {formatCurrencyWithSign(calculateTimeBasedChanges.sinceSixMonthsAgo.change, settings.displayCurrency, 0)} ({formatPercentage(Math.abs(calculateTimeBasedChanges.sinceSixMonthsAgo.percent), 0)})
                </span>
              ) : (
                <span style={{ fontSize: '15px', color: '#9ca3af' }}>n/a</span>
              )}
            </div>

            {/* Last year */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
              <span className="header-period" style={{ fontSize: '11px', textTransform: 'uppercase' }}>Last Year</span>
              {calculateTimeBasedChanges.sinceYearAgo ? (
                <span className={`header-change ${calculateTimeBasedChanges.sinceYearAgo.change >= 0 ? 'positive' : 'negative'}`} style={{ fontSize: '15px' }}>
                  {calculateTimeBasedChanges.sinceYearAgo.change >= 0 ? <ArrowTrendingUpIcon style={{ width: 14, height: 14 }} /> : <ArrowTrendingDownIcon style={{ width: 14, height: 14 }} />}
                  {formatCurrencyWithSign(calculateTimeBasedChanges.sinceYearAgo.change, settings.displayCurrency, 0)} ({formatPercentage(Math.abs(calculateTimeBasedChanges.sinceYearAgo.percent), 0)})
                </span>
              ) : (
                <span style={{ fontSize: '15px', color: '#9ca3af' }}>n/a</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Chart - Portfolio Value with Projections */}
      <div className="main-chart-card">
        <div className="flex justify-between items-center mb-4 px-6 pt-4">
          <h3 className="text-sm font-semibold text-gray-900">Net Worth Over Time & Projections</h3>
          <div className="flex items-center gap-4">
            {/* Account Filter */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-600">Accounts:</span>
              <div className="relative">
                <button
                  onClick={() => setIsAccountDropdownOpen(!isAccountDropdownOpen)}
                  className="relative cursor-pointer rounded-lg bg-white py-1.5 pl-3 pr-8 text-left border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs min-w-[120px]"
                >
                  <span className="block truncate">
                    {(() => {
                      if (selectedAccountIds === null) return 'All Accounts';
                      if (selectedAccountIds.length === portfolios.length) return 'All Accounts';
                      if (selectedAccountIds.length === 1) {
                        return portfolios.find(p => p.id === selectedAccountIds[0])?.name || 'Selected';
                      }

                      const accountTypes = [
                        { id: 'ISA', name: 'ISA' },
                        { id: 'SIPP', name: 'SIPP' },
                        { id: 'PENSION', name: 'Pension' },
                        { id: 'SAVINGS', name: 'Savings' },
                        { id: 'GIA', name: 'GIA' },
                        { id: 'OTHER', name: 'Other' },
                      ];

                      for (const type of accountTypes) {
                        const typeAccounts = portfolios.filter(p => p.type === type.id);
                        const typeAccountIds = typeAccounts.map(p => p.id);
                        if (typeAccountIds.length > 0 &&
                            typeAccountIds.length === selectedAccountIds.length &&
                            typeAccountIds.every(id => selectedAccountIds.includes(id))) {
                          return `${type.name} (${typeAccountIds.length})`;
                        }
                      }

                      return `${selectedAccountIds.length} Accounts`;
                    })()}
                  </span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                  </span>
                </button>
                <Transition
                  show={isAccountDropdownOpen}
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                  afterLeave={() => setIsAccountDropdownOpen(false)}
                >
                  <div className="absolute z-10 mt-1 max-h-80 w-64 overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none text-xs right-0">
                    {/* All Accounts Option */}
                    <div
                      onClick={() => setSelectedAccountIds(null)}
                      className="relative cursor-pointer select-none py-2 pl-8 pr-4 hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        checked={selectedAccountIds === null}
                        onChange={() => {}}
                        className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className={`block font-semibold ${selectedAccountIds === null ? 'text-gray-900' : 'text-gray-700'}`}>
                        All Accounts
                      </span>
                    </div>

                    {/* By Account Type */}
                    <div className="border-t border-gray-200 my-1"></div>
                    <div className="px-3 py-1 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                      By Type
                    </div>
                    {[
                      { id: 'ISA', name: 'ISA' },
                      { id: 'SIPP', name: 'SIPP' },
                      { id: 'PENSION', name: 'Pension' },
                      { id: 'SAVINGS', name: 'Savings' },
                      { id: 'GIA', name: 'GIA' },
                      { id: 'OTHER', name: 'Other' },
                    ].map((type) => {
                      const accountsOfType = portfolios.filter(p => p.type === type.id);
                      const typeAccountIds = accountsOfType.map(p => p.id);
                      const isTypeSelected = selectedAccountIds !== null &&
                        typeAccountIds.length > 0 &&
                        typeAccountIds.every(id => selectedAccountIds.includes(id)) &&
                        selectedAccountIds.every(id => typeAccountIds.includes(id));

                      if (accountsOfType.length === 0) return null;

                      return (
                        <div
                          key={type.id}
                          onClick={() => setSelectedAccountIds(typeAccountIds.length > 0 ? typeAccountIds : null)}
                          className="relative cursor-pointer select-none py-2 pl-8 pr-4 hover:bg-gray-50"
                        >
                          <input
                            type="checkbox"
                            checked={isTypeSelected}
                            onChange={() => {}}
                            className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className={`block ${isTypeSelected ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                            {type.name} ({accountsOfType.length})
                          </span>
                        </div>
                      );
                    })}

                    <div className="border-t border-gray-200 my-1"></div>
                    <div className="px-3 py-1 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                      Individual Accounts
                    </div>
                    {[...portfolios].sort((a, b) => a.name.localeCompare(b.name)).map((portfolio) => {
                      const isSelected = selectedAccountIds !== null && selectedAccountIds.includes(portfolio.id);
                      const portfolioValue = convertCurrency(portfolio.currentValue, portfolio.currency, settings.displayCurrency, exchangeRates);
                      const monthlyContribution = portfolio.monthlyContribution || 0;

                      return (
                        <div
                          key={portfolio.id}
                          onClick={() => {
                            if (selectedAccountIds === null) {
                              setSelectedAccountIds([portfolio.id]);
                            } else if (selectedAccountIds.includes(portfolio.id)) {
                              const newSelection = selectedAccountIds.filter(id => id !== portfolio.id);
                              setSelectedAccountIds(newSelection.length === 0 ? null : newSelection);
                            } else {
                              const newSelection = [...selectedAccountIds, portfolio.id];
                              setSelectedAccountIds(newSelection.length === portfolios.length ? null : newSelection);
                            }
                          }}
                          className="relative cursor-pointer select-none py-2 pl-8 pr-4 hover:bg-gray-50"
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {}}
                            className="absolute left-2 top-3 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <div className="flex flex-col gap-0.5">
                            <span className={`block font-medium ${isSelected ? 'text-gray-900' : 'text-gray-700'}`}>
                              {portfolio.name}
                            </span>
                            <div className="flex items-center gap-3 text-xs text-gray-500">
                              <span className="font-semibold text-gray-700">{formatCurrency(portfolioValue, settings.displayCurrency, 0)}</span>
                              {monthlyContribution > 0 && (
                                <span>+{formatCurrency(monthlyContribution, portfolio.currency, 0)}/mo</span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Transition>
              </div>
            </div>

            {/* Period */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-600">Period:</span>
              <Listbox value={projectionYears} onChange={setProjectionYears}>
                <div className="relative">
                  <Listbox.Button className="relative cursor-pointer rounded-lg bg-white py-1.5 pl-3 pr-8 text-left border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs min-w-[80px]">
                    <span className="block truncate">{projectionYears} {projectionYears === 1 ? 'Year' : 'Years'}</span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                      <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                    </span>
                  </Listbox.Button>
                  <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-32 overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none text-xs right-0">
                      {[1, 3, 5, 10, 15, 20, 30].map((years) => (
                        <Listbox.Option
                          key={years}
                          value={years}
                          className={({ active }) =>
                            `relative cursor-pointer select-none py-2 pl-8 pr-4 ${
                              active ? 'bg-blue-50 text-blue-900' : 'text-gray-900'
                            }`
                          }
                        >
                          {({ selected }) => (
                            <>
                              <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                {years} {years === 1 ? 'Year' : 'Years'}
                              </span>
                              {selected && (
                                <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-blue-600">
                                  <CheckIcon className="h-4 w-4" />
                                </span>
                              )}
                            </>
                          )}
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </Transition>
                </div>
              </Listbox>
            </div>

            {/* Breakdown */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-600">Breakdown:</span>
              <Listbox value={chartAggregation} onChange={setChartAggregation}>
                <div className="relative">
                  <Listbox.Button className="relative cursor-pointer rounded-lg bg-white py-1.5 pl-3 pr-8 text-left border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs min-w-[80px]">
                    <span className="block truncate capitalize">{chartAggregation}</span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                      <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                    </span>
                  </Listbox.Button>
                  <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-32 overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none text-xs right-0">
                      {['monthly', 'yearly'].map((agg) => (
                        <Listbox.Option
                          key={agg}
                          value={agg}
                          className={({ active }) =>
                            `relative cursor-pointer select-none py-2 pl-8 pr-4 ${
                              active ? 'bg-blue-50 text-blue-900' : 'text-gray-900'
                            }`
                          }
                        >
                          {({ selected }) => (
                            <>
                              <span className={`block truncate capitalize ${selected ? 'font-semibold' : 'font-normal'}`}>
                                {agg}
                              </span>
                              {selected && (
                                <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-blue-600">
                                  <CheckIcon className="h-4 w-4" />
                                </span>
                              )}
                            </>
                          )}
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </Transition>
                </div>
              </Listbox>
            </div>
          </div>
        </div>
        <div className="flex gap-4 text-xs mb-3 justify-end px-6">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#0ea5e9' }}></div>
            <span className="text-gray-600">Current Value</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#64748b' }}></div>
            <span className="text-gray-600">Cost Basis</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#f59e0b' }}></div>
            <span className="text-gray-600">Low ({formatPercentage(lowReturnRate, 1)})</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#8b5bf6' }}></div>
            <span className="text-gray-600">Average ({formatPercentage(avgReturnRate, 1)})</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#059669' }}></div>
            <span className="text-gray-600">High ({formatPercentage(optReturnRate, 1)})</span>
          </div>
        </div>
        {combinedChartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart data={combinedChartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }} isAnimationActive={false}>
              <defs>
                <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.5}/>
                  <stop offset="50%" stopColor="#38bdf8" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#bae6fd" stopOpacity={0.1}/>
                </linearGradient>
                <linearGradient id="colorCostBasis" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#64748b" stopOpacity={0.5}/>
                  <stop offset="50%" stopColor="#94a3b8" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#cbd5e1" stopOpacity={0.1}/>
                </linearGradient>
                <linearGradient id="colorLow" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.5}/>
                  <stop offset="50%" stopColor="#fbbf24" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#fde68a" stopOpacity={0.1}/>
                </linearGradient>
                <linearGradient id="colorAverage" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.5}/>
                  <stop offset="50%" stopColor="#a78bfa" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#c4b5fd" stopOpacity={0.1}/>
                </linearGradient>
                <linearGradient id="colorOptimistic" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#059669" stopOpacity={0.5}/>
                  <stop offset="50%" stopColor="#10b981" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#6ee7b7" stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <XAxis
                dataKey="displayDate"
                stroke="#9ca3af"
                style={{ fontSize: '11px' }}
                tick={{ fill: '#6b7280' }}
                interval="preserveStartEnd"
              />
              <YAxis
                stroke="#9ca3af"
                style={{ fontSize: '11px' }}
                tick={{ fill: '#6b7280' }}
                tickFormatter={(value) => formatCurrency(value, settings.displayCurrency, 0)}
              />
              <Tooltip
                allowEscapeViewBox={{ x: false, y: true }}
                wrapperStyle={{ zIndex: 1000, pointerEvents: 'none' }}
                position={{ y: 0 }}
                content={({ active, payload, label, coordinate, viewBox }) => {
                  if (!active || !payload) return null;

                  const currentIndex = combinedChartData.findIndex(d => d.displayDate === label);
                  const previousData = currentIndex > 0 ? combinedChartData[currentIndex - 1] : null;

                  const uniqueData = {};
                  payload.forEach(entry => {
                    if (entry.value != null && !uniqueData[entry.dataKey]) {
                      uniqueData[entry.dataKey] = entry;
                    }
                  });

                  const labels = {
                    total: 'Current Value',
                    costBasis: 'Cost Basis',
                    lowProjection: 'Low',
                    averageProjection: 'Average',
                    optimisticProjection: 'High',
                  };

                  const isNearRightEdge = coordinate && viewBox && (viewBox.x + viewBox.width - coordinate.x < 200);

                  return (
                    <div style={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: 8,
                      fontSize: '13px',
                      padding: '8px 12px',
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                      transform: isNearRightEdge ? 'translateX(-100%)' : 'translateX(10px)',
                      marginLeft: isNearRightEdge ? '-10px' : '0px',
                    }}>
                      <p style={{ marginBottom: '4px', fontWeight: 600 }}>{label}</p>
                      {Object.values(uniqueData).map((entry, index) => {
                        const currentValue = entry.value;
                        const previousValue = previousData?.[entry.dataKey];
                        const change = previousValue != null ? currentValue - previousValue : null;
                        const percentChange = previousValue != null && previousValue !== 0
                          ? ((currentValue - previousValue) / previousValue) * 100
                          : null;

                        return (
                          <div key={index} style={{ margin: '4px 0' }}>
                            <p style={{ margin: 0, color: entry.color }}>
                              <span style={{ fontWeight: 500 }}>{labels[entry.dataKey] || entry.dataKey}:</span>{' '}
                              {formatCurrency(currentValue, settings.displayCurrency, 0)}
                            </p>
                            {change != null && (
                              <p style={{
                                margin: 0,
                                marginLeft: '8px',
                                fontSize: '11px',
                                color: change >= 0 ? '#10b981' : '#ef4444',
                                fontWeight: 500
                              }}>
                                {change >= 0 ? '\u2191' : '\u2193'} {formatCurrency(Math.abs(change), settings.displayCurrency, 0)} ({formatPercentageWithSign(percentChange, 1)})
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                }}
              />
              {/* Historical areas */}
              <Area type="monotone" dataKey="costBasis" stroke="none" fill="url(#colorCostBasis)" fillOpacity={1} isAnimationActive={false} />
              <Area type="monotone" dataKey="total" stroke="none" fill="url(#colorTotal)" fillOpacity={1} isAnimationActive={false} />
              {/* Projection areas */}
              <Area type="monotone" dataKey="lowProjection" stroke="none" fill="url(#colorLow)" fillOpacity={1} isAnimationActive={false} />
              <Area type="monotone" dataKey="averageProjection" stroke="none" fill="url(#colorAverage)" fillOpacity={1} isAnimationActive={false} />
              <Area type="monotone" dataKey="optimisticProjection" stroke="none" fill="url(#colorOptimistic)" fillOpacity={1} isAnimationActive={false} />
              {/* Historical lines */}
              <Line type="monotone" dataKey="costBasis" stroke="#64748b" strokeWidth={2} dot={false} connectNulls={false} isAnimationActive={false} />
              <Line type="monotone" dataKey="total" stroke="#0ea5e9" strokeWidth={3} dot={false} connectNulls={false} isAnimationActive={false} />
              {/* Projection lines */}
              <Line type="monotone" dataKey="lowProjection" stroke="#f59e0b" strokeWidth={3} strokeDasharray="5 5" dot={false} connectNulls={true} isAnimationActive={false} />
              <Line type="monotone" dataKey="averageProjection" stroke="#8b5cf6" strokeWidth={3} strokeDasharray="5 5" dot={false} connectNulls={true} isAnimationActive={false} />
              <Line type="monotone" dataKey="optimisticProjection" stroke="#059669" strokeWidth={3} strokeDasharray="5 5" dot={false} connectNulls={true} isAnimationActive={false} />
              {totalGoalTarget > 0 && (
                <ReferenceLine
                  y={totalGoalTarget}
                  stroke="#10b981"
                  strokeDasharray="5 5"
                  strokeWidth={2}
                  label={{ value: 'Goal Target', position: 'right', fill: '#10b981', fontSize: 12 }}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <div className="empty-state">
            <p style={{ color: '#9ca3af' }}>
              Update account values to see trends over time
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports;
